import { TeamClient } from "@formant/core/client/TeamClient";
import { defined } from "@formant/core/common/defined";
import { ITeam } from "@formant/core/model/ITeam";
import { IUser } from "@formant/core/model/IUser";
import { Uuid } from "@formant/core/model/Uuid";

import { IAuthStore } from "./IAuthStore";
import { ITeamStore } from "./ITeamStore";
import { IUserStore } from "./IUserStore";
import { StoreCache } from "./StoreCache";

export class TeamStore implements ITeamStore {
    private cache = new StoreCache<Uuid, ITeam | false>();
    private listCache = new StoreCache<void, ITeam[]>();
    private listUsersCache = new StoreCache<Uuid, IUser[]>();

    constructor(
        private teamClient: TeamClient,
        private authStore: IAuthStore,
        private userStore: IUserStore,
    ) {}

    listAll(): ITeam[] | undefined {
        return this.listCache.get(undefined, async () => this.fetchList());
    }

    get(id: Uuid): ITeam | false | undefined {
        return this.cache.get(id, async () => await this.fetchGet(id));
    }

    listUsers(id: Uuid): IUser[] | undefined {
        return this.listUsersCache.get(
            id,
            async () => await this.fetchUsers(id),
        );
    }

    async fetchGet(id: Uuid): Promise<ITeam | false> {
        const team = await this.teamClient.get(
            await this.authStore.token(),
            id,
        );
        const value = team || false;
        this.cache.set(id, value);
        return value;
    }

    async fetchList(): Promise<ITeam[]> {
        const results = await this.teamClient.list(
            await this.authStore.token(),
        );
        results.forEach(team => this.cache.set(defined(team.id), team));
        this.listCache.set(undefined, results);
        return results;
    }

    async fetchUsers(teamId: string): Promise<IUser[]> {
        const results = await this.teamClient.getUsers(
            await this.authStore.token(),
            teamId,
        );
        this.listUsersCache.set(teamId, results);
        return results;
    }

    async save(team: ITeam, userIds?: string[]) {
        const saved =
            team.id === undefined
                ? await this.create(team)
                : await this.update(team.id, team);
        if (userIds) {
            await this.updateUsersInTeam(saved, userIds);
        }
        return saved;
    }

    async create(team: ITeam): Promise<ITeam> {
        const created = await this.teamClient.create(
            await this.authStore.token(),
            {
                organizationId: defined(this.authStore.organizationId),
                name: team.name,
                roleId: team.roleId,
                tags: team.tags || {},
            },
        );
        this.cache.clear();
        this.listCache.clear();

        return created;
    }

    async update(id: Uuid, team: ITeam) {
        const updated = await this.teamClient.update(
            await this.authStore.token(),
            id,
            team,
        );

        this.cache.clear();
        this.listCache.clear();
        return updated;
    }

    async updateUsersInTeam(team: ITeam, userIds: string[]) {
        const teamUsers = await this.fetchUsers(defined(team.id));
        const removedUserIds =
            teamUsers
                ?.map(_ => defined(_.id))
                ?.filter(_ => _ && !userIds.includes(_)) || [];
        const addedUserIds =
            userIds.filter(
                _ => _ && !teamUsers?.map(user => user.id).includes(_),
            ) || [];

        await this.userStore.addUsersToTeam(defined(team.id), addedUserIds);
        await this.userStore.removeUsersFromTeam(removedUserIds);
        this.cache.clear();
        this.listCache.clear();
        this.listUsersCache.clear();
    }

    async delete(id: Uuid) {
        await this.teamClient.delete(await this.authStore.token(), id);
        this.cache.clear();
        this.listCache.clear();
    }
}
